import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, TrendingUp, MessageSquare, Clock, Target, ChevronDown, ChevronUp } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  not_addressed: '#d4d4d8',
  emerging: '#fbbf24',
  progressing: '#60a5fa',
  mastered: '#34d399'
};

const PIE_COLORS = ['#8b5cf6', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

const STATUS_LABELS = { not_addressed: 'Not Addressed', emerging: 'Emerging', progressing: 'Progressing', mastered: 'Mastered' };

export default function ProgressVisualization({ childId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/progress/${childId}/visualization`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (!data || data.total_sessions === 0) return null;

  // Build goal timeline chart data
  const allGoals = [...new Set(data.goal_timeline.flatMap(t => Object.keys(t.goals)))];
  const timelineData = data.goal_timeline.map(t => {
    const entry = { date: t.date ? new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '' };
    allGoals.forEach(g => {
      entry[g] = t.goals[g]?.status_num ?? null;
    });
    return entry;
  });

  // Shorten goal names for chart labels
  const shortenGoal = (g) => g.length > 35 ? g.substring(0, 32) + '...' : g;

  const goalColors = ['#8b5cf6', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#ec4899'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-white p-3 rounded-xl shadow-lg border border-stone-200 text-xs">
        <p className="font-medium mb-1">{label}</p>
        {payload.filter(p => p.value !== null).map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {shortenGoal(p.dataKey)}: {STATUS_LABELS[Object.keys(STATUS_LABELS)[p.value]] || p.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <Card className="rounded-2xl border border-stone-100 overflow-hidden" data-testid="progress-visualization">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 flex items-center justify-between hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-serif text-primary-900">Progress Tracking</h3>
            <p className="text-sm text-stone-500">{data.total_sessions} sessions, {data.total_chunks} language samples</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-stone-400" /> : <ChevronDown className="w-5 h-5 text-stone-400" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 bg-purple-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-purple-700">{data.total_sessions}</p>
              <p className="text-xs text-purple-600">Sessions</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-blue-700">{data.session_stats.avg_duration}m</p>
              <p className="text-xs text-blue-600">Avg Duration</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-green-700">{data.total_chunks}</p>
              <p className="text-xs text-green-600">Language Samples</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-amber-700">{allGoals.length}</p>
              <p className="text-xs text-amber-600">Goals Tracked</p>
            </div>
          </div>

          {/* Goal Progress Timeline */}
          {timelineData.length > 1 && allGoals.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-600" /> Goal Progress Over Time
              </h4>
              <div className="bg-white rounded-xl border border-stone-100 p-4">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={timelineData} margin={{ left: 5, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis
                      domain={[0, 3]}
                      ticks={[0, 1, 2, 3]}
                      tickFormatter={(v) => ['N/A', 'Emrg', 'Prog', 'Mstd'][v] || ''}
                      tick={{ fontSize: 10 }}
                      width={40}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {allGoals.slice(0, 6).map((goal, i) => (
                      <Line
                        key={goal}
                        type="stepAfter"
                        dataKey={goal}
                        stroke={goalColors[i % goalColors.length]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                        name={shortenGoal(goal)}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 px-2">
                  {allGoals.slice(0, 6).map((goal, i) => (
                    <div key={goal} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: goalColors[i % goalColors.length] }} />
                      <span className="text-xs text-stone-600">{shortenGoal(goal)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Communication Functions + Chunk Stages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.communication_functions.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-600" /> Communication Functions
                </h4>
                <div className="bg-white rounded-xl border border-stone-100 p-4">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={data.communication_functions}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={65}
                        innerRadius={30}
                      >
                        {data.communication_functions.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} samples`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-1 justify-center">
                    {data.communication_functions.map((f, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="text-xs text-stone-600">{f.name} ({f.count})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {data.chunk_growth.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-600" /> Language Sample Growth
                </h4>
                <div className="bg-white rounded-xl border border-stone-100 p-4">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.chunk_growth} margin={{ left: 5, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={30} />
                      <Tooltip />
                      <Bar dataKey="total" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Samples" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          {/* Top Strategies */}
          {data.session_stats.top_strategies.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" /> Most Used Strategies
              </h4>
              <div className="flex flex-wrap gap-2">
                {data.session_stats.top_strategies.map((s, i) => (
                  <span key={i} className="text-xs bg-stone-100 text-stone-700 px-3 py-1.5 rounded-full">
                    {s.name} ({s.count}x)
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
